import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

/**
 * Validator that checks if a control's value matches another control's value
 * @param controlNameToMatch - Name of the control to match against
 * @param errorKey - Optional custom error key (default: 'mismatch')
 * @returns ValidatorFn
 *
 * @example
 * // In component:
 * confirmPassword: new FormControl('', [
 *   Validators.required,
 *   matchFieldValidator('newPassword')
 * ])
 */
export function matchFieldValidator(
  controlNameToMatch: string,
  errorKey: string = "mismatch",
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const parent = control.parent;
    if (!parent) {
      return null; // Can't validate without parent form group
    }

    const controlToMatch = parent.get(controlNameToMatch);
    if (!controlToMatch) {
      return null;
    }

    return control.value === controlToMatch.value ? null : { [errorKey]: true };
  };
}

/**
 * Validator that checks if a control's value is different from another control's value
 * @param controlNameToCompare - Name of the control to compare against
 * @param errorKey - Optional custom error key (default: 'mustBeDifferent')
 * @returns ValidatorFn
 *
 * @example
 * // In component:
 * newPassword: new FormControl('', [
 *   Validators.required,
 *   differentFromFieldValidator('oldPassword')
 * ])
 */
export function differentFromFieldValidator(
  controlNameToCompare: string,
  errorKey: string = "mustBeDifferent",
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const parent = control.parent;
    if (!parent) {
      return null; // Can't validate without parent form group
    }

    const controlToCompare = parent.get(controlNameToCompare);
    if (!controlToCompare) {
      return null;
    }

    // Only validate if the control to compare has a value
    if (!controlToCompare.value) {
      return null;
    }

    return control.value !== controlToCompare.value
      ? null
      : { [errorKey]: true };
  };
}
